import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, CalendarClock, Radio, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { http, errMsg, waLink, useSiteData } from "@/lib/api";
import { PageHeader } from "@/components/site/PageHeader";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { EASE, Eyebrow } from "@/components/site/Reveal";

const fmtDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" }) : "");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

const Stepper = ({ data }) => {
  const stages = Array.isArray(data?.stages) ? data.stages : [];
  const history = Array.isArray(data?.history) ? data.history : [];
  return (
    <ol className="relative mt-10 space-y-0" data-testid="track-stepper">
      {stages.map((s, i) => {
        const done = i < data.stage || data.stage === stages.length - 1;
        const current = i === data.stage;
        const last = [...history].reverse().find((h) => h.stage === i);
        return (
          <li key={s} className="relative flex gap-5 pb-9 last:pb-0" data-testid={`track-stage-${i}`}>
            {i < stages.length - 1 && <span className={`absolute left-[15px] top-8 h-[calc(100%-2rem)] w-px ${i < data.stage ? "bg-[#2DD4BF]" : "bg-[#163D43]"}`} />}
            <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs ${done ? "border-[#2DD4BF] bg-[#2DD4BF] text-[#041214]" : current ? "border-[#2DD4BF] text-[#2DD4BF]" : "border-[#163D43] text-teal-300"}`}>
              {done ? <Check className="h-4 w-4" /> : i + 1}
              {current && !done && <span className="absolute inset-0 animate-ping rounded-full border border-[#2DD4BF]/60" />}
            </span>
            <div className="pt-1">
              <p className={`font-display text-lg font-semibold ${i <= data.stage ? "text-teal-50" : "text-teal-300"}`}>{s}</p>
              {last && <p className="font-mono-x mt-1 text-[11px] uppercase tracking-[0.15em] text-teal-300">{fmtTime(last.at)}</p>}
              {last?.note && <p className="mt-2 rounded-md border border-[#163D43] bg-[#0B2326] px-3 py-2 text-sm text-teal-100">{last.note}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

const Result = ({ data }) => {
  const delivered = data.stage === (data.stages?.length ?? 5) - 1;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="mt-12 grid gap-8 lg:grid-cols-5" data-testid="track-result">
      <div className="rounded-[3px] border border-[#163D43] bg-[#071D1F] p-7 lg:col-span-2">
        <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-[#2DD4BF]" data-testid="track-code">{data.code}</p>
        <h2 className="font-display mt-3 text-3xl font-semibold text-teal-50">{data.service_title}</h2>
        <p className="mt-1 text-teal-200">Cliente: {data.client_first_name}</p>
        <div className="mt-8 border-t border-[#163D43] pt-6">
          <p className="font-mono-x flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-teal-300"><CalendarClock className="h-4 w-4" /> Prazo de entrega</p>
          <p className="font-display mt-2 text-2xl text-teal-50 first-letter:uppercase" data-testid="track-due-date">{fmtDate(data.due_date)}</p>
          <p className="mt-1 text-sm text-teal-200">{data.deadline_days} dias úteis a partir de {fmtDate(data.start_date)} · sem contar fins de semana e feriados nacionais</p>
          <p className="mt-5 inline-block rounded-full bg-[#2DD4BF]/10 px-4 py-2 text-sm text-[#2DD4BF]" data-testid="track-days-left">
            {delivered ? "Pedido entregue" : data.business_days_left > 0 ? `Faltam ${data.business_days_left} dia(s) úteis` : "Entrega prevista para hoje"}
          </p>
        </div>
      </div>
      <div className="lg:col-span-3">
        <p className="font-mono-x flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-teal-300" data-testid="track-live">
          <Radio className="h-4 w-4 animate-pulse text-[#2DD4BF]" /> Atualização em tempo real · {fmtTime(data.updated_at)}
        </p>
        <Stepper data={data} />
      </div>
    </motion.div>
  );
};

export default function Track() {
  const { code } = useParams();
  const nav = useNavigate();
  const { settings } = useSiteData();
  const [input, setInput] = useState(code || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!code) return undefined;
    let stop = false;
    const load = () =>
      http.get(`/track/${encodeURIComponent(code)}`).then((r) => { if (!stop) { setData(r.data); setError(""); } }).catch((e) => { if (!stop) { setData(null); setError(errMsg(e)); } });
    load();
    const t = setInterval(load, 10000);
    return () => { stop = true; clearInterval(t); };
  }, [code]);
  const submit = (e) => {
    e.preventDefault();
    if (input.trim()) nav(`/acompanhar/${input.trim().toUpperCase()}`);
  };
  return (
    <div className="grain relative min-h-screen bg-[#041214] pb-24" data-testid="track-page">
      <PageHeader backTo="/" />
      <WhatsAppFloat settings={settings} />
      <div className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-[#0D9488]/20 blur-[140px]" />
      <div className="relative mx-auto max-w-[1200px] px-5 pt-32 md:px-10">
        <Eyebrow>Área do cliente</Eyebrow>
        <h1 className="mt-6 text-5xl font-semibold uppercase leading-[0.9] text-teal-50 sm:text-6xl lg:text-7xl">
          <span className="block overflow-hidden"><motion.span className="font-display block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: EASE }}>Acompanhe</motion.span></span>
          <span className="block overflow-hidden"><motion.span className="font-serif-i block normal-case text-[#2DD4BF]" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease: EASE, delay: 0.12 }}>seu pedido.</motion.span></span>
        </h1>
        <form onSubmit={submit} className="mt-10 flex max-w-xl gap-3" data-testid="track-form">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Código do pedido (ex: SD-4K7P2)" data-testid="track-code-input" className="h-12 border-[#163D43] bg-[#071D1F] uppercase text-teal-50 placeholder:normal-case" />
          <button data-testid="track-submit" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#2DD4BF] px-6 font-semibold text-[#041214] transition-transform hover:-translate-y-0.5">
            <Search className="h-4 w-4" /> Buscar
          </button>
        </form>
        <p className="mt-3 text-sm text-teal-300">O código é enviado pelo WhatsApp após a confirmação do pedido.</p>
        {error && <p className="mt-8 text-red-300" data-testid="track-error">{error}</p>}
        {data && <Result data={data} />}
        <a href={waLink(settings.whatsapp, `Olá! Quero falar sobre meu pedido ${code || ""}.`)} target="_blank" rel="noreferrer" className="mt-12 inline-block text-sm text-teal-300 underline-offset-4 hover:text-[#2DD4BF] hover:underline" data-testid="track-help">
          Dúvidas? Fale com a gente no WhatsApp
        </a>
      </div>
    </div>
  );
}
